// Felles «AVSENDER · ENHET · DTG · POSISJON»-blokk for ALLE rapportskjemaer.
//
// HVORFOR én blokk og ikke fire like: dette var samme felt fire steder, skrevet
// fire ganger, med fire ulike (og noen ganger manglende) verdier. Etikettene,
// rekkefølgen og — viktigst — KILDEMERKINGA av posisjonen skal se helt lik ut i
// stridsmelding, PERSTAT, LOGSTAT og RFI. Ellers lærer operatøren fire skjemaer
// i stedet for ett.
//
// Verdiene kommer fra `kontekst.ts` (én kilde). Blokka gjør bare tre ting:
//  1) viser AVSENDER/ENHET/DTG ferdig utfylt og redigerbare, og POSISJON
//     ferdig utfylt og READ-ONLY (se punkt 3),
//  2) frisker opp DTG ved hver visning — men RØRER ALDRI et felt operatøren
//     har skrevet i (da hadde skjemaet slettet arbeidet hans),
//  3) merker posisjonsfeltet med KILDE. Feltet er read-only fordi verdien som
//     faktisk sendes ALLTID kommer fra `egenposisjon()` (fersk GPS, ellers
//     kartsenter) — aldri fra det operatøren skriver her. Et redigerbart felt
//     ville sett ut som en korrigeringsmulighet det stille IKKE var; å skrive
//     i det ville forsvunnet sporløst ved innsending. Se `main.ts` (`withPos`
//     i casualty-/logstat-wiringa) for hvor den ekte posisjonen faktisk hentes.
import type { Kontekst } from '../kontekst.js';
import { KONTEKST_FELT } from '../kontekst.js';

export interface KontekstBlokkOpts {
  /** Fersk kontekst. Kalles ved hver `oppfrisk()` — tida sendes inn der. */
  hent: () => Kontekst;
  /** Ta med posisjonsraden? Av for skjemaer uten stedfesting (RFI). */
  posisjon?: boolean;
  /** Etikett på posisjonsraden (LOGSTAT kaller den «DZ / leveringssted»). */
  posisjonLabel?: string;
  /**
   * Ledetekst øverst i blokka — sier hva GRUPPEN er, ikke bare hva feltene
   * heter. UX-funn 7: avsenderposisjon og observasjonssted smeltet sammen
   * fordi begge bare het «Posisjon». Skjemaer med et eget HVOR-felt (strids-
   * melding/SALUTE) sender inn en tekst som trekker grensen; skjemaer uten
   * (PERSTAT/LOGSTAT/RFI) trenger den ikke og utelater den.
   */
  ledetekst?: string;
}

export class KontekstBlokk {
  readonly el: HTMLElement;
  private inputs = new Map<string, HTMLInputElement>();
  /** Felt operatøren har skrevet i — de skal aldri overskrives av en oppfrisking. */
  private rort = new Set<string>();
  private posNote!: HTMLElement;

  constructor(private opts: KontekstBlokkOpts) {
    this.el = document.createElement('div');
    this.el.className = 'kontekst-blokk';
    if (opts.ledetekst) {
      const l = document.createElement('span');
      l.className = 'kontekst-ledetekst';
      l.textContent = opts.ledetekst; // kan bære feltnavn — textContent, aldri innerHTML
      this.el.append(l);
    }
    const rader: Array<[string, string]> = [
      [KONTEKST_FELT.avsender, 'Avsender'],
      [KONTEKST_FELT.enhet, 'Enhet'],
      [KONTEKST_FELT.dtg, 'DTG'],
    ];
    if (opts.posisjon !== false) rader.push([KONTEKST_FELT.posisjon, opts.posisjonLabel ?? 'Posisjon']);
    for (const [key, label] of rader) {
      const wrap = document.createElement('div');
      wrap.className = 'report-field kontekst-felt';
      const l = document.createElement('span');
      l.className = 'report-field-label';
      l.textContent = label;
      const input = document.createElement('input');
      input.className = 'report-input';
      input.dataset.kontekst = key;
      if (key === KONTEKST_FELT.posisjon) {
        // Posisjonen er ALDRI operatørens å skrive: verdien som faktisk følger
        // meldingen ut kommer alltid fra `egenposisjon()` (se `main.ts`), aldri
        // fra dette feltet. Read-only, så feltet viser nøyaktig det det er —
        // en kildemerket visning — og ikke later som en redigering betyr noe.
        input.readOnly = true;
        input.tabIndex = -1;
        input.classList.add('kontekst-readonly');
      } else {
        // Skriver operatøren selv, eier han feltet fra da av.
        input.addEventListener('input', () => this.rort.add(key));
      }
      this.inputs.set(key, input);
      wrap.append(l, input);
      this.el.append(wrap);
    }
    // Kildelinja for posisjonen: står ALLTID der posisjonsraden står, så det
    // aldri er tvil om hvor koordinatet kommer fra.
    this.posNote = document.createElement('span');
    this.posNote.className = 'checklist-note kontekst-poskilde';
    if (opts.posisjon !== false) this.el.append(this.posNote);
    this.oppfrisk();
  }

  /** Fyll (eller frisk opp) fra kontekst. Urørte felt får nye verdier. */
  oppfrisk(): void {
    const k = this.opts.hent();
    this.sett(KONTEKST_FELT.avsender, k.avsenderOgEnhet || k.avsender);
    this.sett(KONTEKST_FELT.enhet, k.enhet);
    this.sett(KONTEKST_FELT.dtg, k.dtg);
    const pos = this.inputs.get(KONTEKST_FELT.posisjon);
    if (pos) {
      this.sett(KONTEKST_FELT.posisjon, k.posisjonMerket);
      // ⚠ Ukjent posisjon skal IKKE se ut som en tom rubrikk å fylle med noe
      // plausibelt. Den sier hva som mangler.
      pos.placeholder = k.posisjonMerket ? '' : 'ukjent — ingen GPS';
      pos.classList.toggle('kontekst-ukjent', !k.posisjonMerket);
      this.posNote.textContent = `Kilde: ${k.posMerknad}`;
      this.posNote.classList.toggle('kontekst-advarsel', k.posKilde !== 'gps');
    }
  }

  private sett(key: string, verdi: string) {
    const input = this.inputs.get(key);
    if (!input || this.rort.has(key)) return;
    input.value = verdi;
  }

  /** Verdiene som skal følge meldingen ut. Tomme felt utelates. */
  felt(): Record<string, string> {
    const ut: Record<string, string> = {};
    for (const [key, input] of this.inputs) {
      const v = input.value.trim();
      if (v) ut[key] = v;
    }
    return ut;
  }

  /** Enkeltverdi (f.eks. enhet, som PERSTAT/LOGSTAT trenger i sitt eget skjema). */
  verdi(key: string): string {
    return this.inputs.get(key)?.value.trim() ?? '';
  }
}
